import React, { useState } from 'react';
import { FormInstance } from 'antd/es/form';
import { get } from 'lodash';
import PrivacyModal from 'components/modal/PrivacyModal';
import TermsAndConditionsModal from 'components/modal/TermsAndConditionsModal';
import SMSVerificationModal from 'components/user/SMSVerificationModal';
import { moveToFirstError } from 'libs/api/errorHandle';
import { checkDuplicateUserid } from 'libs/api/user';
import { TModalState } from 'modules/types';
import { CUserType, CUserTypeColor } from 'constants/user.const';
import { EUserType } from 'enums/base.enum';
import { EMentoringField } from 'enums/mentoring.enum';
import { Radio, Button, Form, Tooltip, Spin, Typography, Input, Checkbox, Tag, message } from 'antd';
import { CheckOutlined, CheckSquareOutlined } from '@ant-design/icons';
import { SecondaryColor, FormWrapper } from 'GlobalStyles';

const { Text, Title } = Typography;
type TAccountSettingProps = {
  userType: EUserType;
  form: FormInstance;
};

const AccountSetting = ({ userType, form }: TAccountSettingProps) => {
  const [privacyModal, setPrivacyModal] = useState(false);
  const [isVerified, setIsVerified] = useState(
    form.getFieldValue('is_phone_verified') ? form.getFieldValue('is_phone_verified') : false,
  );
  const [termsAndConditionsModal, setTermsAndConditionsModal] = useState(false);
  const [smsVerificationModal, setSMSVerificationModal] = useState<TModalState>({
    record: '',
    visible: false,
  });
  const [duplicateChecked, setDuplicateChecked] = useState(false);

  async function handleCheckDuplicateUserid() {
    try {
      const response = await checkDuplicateUserid(form.getFieldValue('userid'));
      if (get(response, 'data') === true) {
        setDuplicateChecked(true);
        message.info('사용 가능한 아이디입니다.');
        form.setFields([{ name: 'userid', errors: [], validating: true }]);
      } else {
        setDuplicateChecked(false);
        message.error('이미 가입된 아이디입니다.');
      }
    } catch (e) {
      throw e;
    }
  }

  return (
    <FormWrapper style={{ maxWidth: '380px', paddingTop: '0px' }}>
      <Tag color={CUserTypeColor[userType]}>{CUserType[userType]}</Tag>
      <Title level={2}>
        <span style={{ color: `${CUserTypeColor[userType]}` }}>{CUserType[userType]}</span>
        로<br />
        가입을 진행합니다.
      </Title>
      {isVerified === true ? (
        <Button
          style={{
            width: '100%',
            height: '45px',
            margin: '20px auto',
          }}
          type='default'
          size='large'
        >
          <CheckOutlined />
          본인인증 완료
        </Button>
      ) : (
        <Tooltip
          placement='top'
          title={
            <span style={{ fontSize: '12px' }}>
              Career Step은 신뢰도 높은 멘토링 제공을 위해 본인인증이 필요합니다.
            </span>
          }
          color={`${SecondaryColor}` as 'green'}
        >
          <Button
            style={{
              width: '100%',
              height: '45px',
              margin: '20px auto',
              border: `1px solid ${SecondaryColor}`,
            }}
            type='default'
            size='large'
            onClick={() => {
              // setIsVerified(true);
              setSMSVerificationModal({
                record: { type: 'signup' },
                visible: true,
                onFinish: (data: string) => {
                  //
                  form.setFields([{ name: 'phone', value: data }]);
                  setIsVerified(true);
                  form.setFields([{ name: 'is_phone_verified', value: true }]);
                },
              });
            }}
          >
            <span style={{ color: `${SecondaryColor}` }}>본인인증 하기</span>
          </Button>
        </Tooltip>
      )}
      <Spin indicator={<div></div>} spinning={!isVerified}>
        <Form
          name='account_setting'
          form={form}
          layout='vertical'
          initialValues={{ receive_marketing_info: false }}
          className='form-inline'
        >
          <Form.Item
            name={'userid'}
            className='form-item'
            label='이메일'
            validateFirst={true}
            rules={[
              {
                type: 'email',
                message: '이메일 형식이 올바르지 않습니다',
              },
              { required: true },
              ({ getFieldValue }) => ({
                validator(rule, value) {
                  if (duplicateChecked === true) {
                    return Promise.resolve();
                  }
                  return Promise.reject('아이디 중복확인을 해주세요.');
                },
              }),
            ]}
          >
            <Input
              size='large'
              placeholder='아이디로 사용됩니다'
              suffix={
                <>
                  {duplicateChecked === false ? (
                    <div
                      className='hoverable'
                      onClick={() => {
                        // userid의 validate를 먼저 체크하고 중복확인 api를 call함.
                        form
                          .validateFields(['userid'])
                          .then(() => {
                            handleCheckDuplicateUserid();
                          })
                          .catch((errorInfo) => {
                            // error가 중복확인인 경우 api call을 수행.
                            const errorMessage = get(errorInfo.errorFields[0], 'errors', null);
                            if (String(errorMessage).includes('중복')) {
                              handleCheckDuplicateUserid();
                            }
                          });
                      }}
                    >
                      중복확인
                    </div>
                  ) : (
                    <div>
                      <CheckOutlined />
                      확인완료
                    </div>
                  )}
                </>
              }
              onChange={() => {
                setDuplicateChecked(false);
              }}
            />
          </Form.Item>
          <Form.Item
            name={'password'}
            className='form-item'
            label='비밀번호'
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(rule, value) {
                  if (value && value.length >= 8) {
                    return Promise.resolve();
                  }
                  return Promise.reject('비밀번호는 8자리 이상이어야 합니다.');
                },
              }),
            ]}
          >
            <Input.Password size='large' type='password' placeholder='비밀번호를 입력해주세요' />
          </Form.Item>
          <Form.Item
            name={'password_check'}
            className='form-item'
            label='비밀번호 확인'
            dependencies={['password']}
            hasFeedback
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(rule, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject('비밀번호가 일치하지 않습니다.');
                },
              }),
            ]}
          >
            <Input.Password size='large' type='password' placeholder='비밀번호를 한번더 입력해주세요' />
          </Form.Item>
          {userType === EUserType.Mentor ? (
            <div className='label-top'>멘토링 가능하신 영역</div>
          ) : (
            <div className='label-top'>우선순위 관심 영역</div>
          )}
          {userType === EUserType.Mentor ? (
            <Form.Item name='mentoring_field' rules={[{ required: true }]}>
              <Radio.Group style={{ marginLeft: '11px' }}>
                <Radio value={EMentoringField.Consulting}>Consulting</Radio>
                {/* <Radio value={EMentoringField.Finance}>Finance</Radio>
                <Radio value={EMentoringField.Tech}>Tech</Radio> */}
              </Radio.Group>
            </Form.Item>
          ) : (
            <Form.Item name='primary_field' rules={[{ required: true }]}>
              <Radio.Group style={{ marginLeft: '11px' }}>
                <Radio value={EMentoringField.Consulting}>Consulting</Radio>
                {/* <Radio value={EMentoringField.Finance}>Finance</Radio>
                <Radio value={EMentoringField.Tech}>Tech</Radio> */}
              </Radio.Group>
            </Form.Item>
          )}
          <br />
          <Form.Item
            name={'phone'}
            className='form-item'
            label='휴대폰 번호'
            getValueFromEvent={(event) => {
              return event.target.value.replace(/[^\d]/g, '');
            }}
            rules={[{ required: true }]}
          >
            <Input
              size='large'
              className='force-black'
              placeholder='휴대폰 번호를 입력해주세요'
              suffix='인증완료'
              disabled
            />
          </Form.Item>
          <Form.Item name={'is_phone_verified'} hidden={true} noStyle></Form.Item>
          <Form.Item name={'agreement_privacy'} className='form-bottom' valuePropName={'checked'}>
            <Checkbox style={{ fontSize: '13px', width: '100%' }}>
              개인정보 취급방침 동의
              <Button
                style={{ marginLeft: '20px', float: 'right' }}
                size='small'
                onClick={() => {
                  setPrivacyModal(true);
                }}
              >
                내용보기
              </Button>
            </Checkbox>
          </Form.Item>
          <Form.Item name={'agreement_terms'} className='form-bottom' valuePropName={'checked'}>
            <Checkbox style={{ fontSize: '13px', width: '100%' }}>
              이용약관 동의
              <Button
                style={{ marginLeft: '20px', float: 'right' }}
                size='small'
                onClick={() => {
                  setTermsAndConditionsModal(true);
                }}
              >
                내용보기
              </Button>
            </Checkbox>
          </Form.Item>
          <Form.Item name={'receive_marketing_info'} className='form-bottom' valuePropName={'checked'}>
            <Checkbox style={{ fontSize: '13px', width: '100%' }}>
              마케팅 알림 수신
              <span style={{ color: '#a6a6a6' }}>&nbsp;(선택)</span>
            </Checkbox>
          </Form.Item>
          <div style={{ fontSize: '12px', marginTop: '10px' }}>
            <span style={{ color: '#de4949' }}>
              <CheckSquareOutlined />
              &nbsp;꼭 읽어보세요
            </span>
            <br />
            마케팅 알림을 수신하셔야, 수업 시작 전 알림, 멘토 피드백 메일 수신, 커리어스텝의 각종 프로모션
            이메일을 받아보실 수 있습니다
          </div>
          <Form.Item shouldUpdate={true} noStyle={true}>
            {() => (
              <div className='steps-action'>
                <Button
                  type='primary'
                  htmlType='submit'
                  size='large'
                  disabled={
                    !form.getFieldValue('agreement_terms') || !form.getFieldValue('agreement_privacy')
                  }
                  onClick={() => {
                    form.validateFields().catch((errorInfo) => {
                      moveToFirstError(form, errorInfo.errorFields);
                    });
                  }}
                >
                  다음
                </Button>
              </div>
            )}
          </Form.Item>
        </Form>
      </Spin>
      <PrivacyModal visible={privacyModal} setVisible={setPrivacyModal} />
      <TermsAndConditionsModal visible={termsAndConditionsModal} setVisible={setTermsAndConditionsModal} />
      <SMSVerificationModal modalState={smsVerificationModal} setModalState={setSMSVerificationModal} />
    </FormWrapper>
  );
};

export default AccountSetting;
